import React from 'react';
import {
    showNiceDate,
    showTime,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import { IntraDayProfileDataState } from 'widgets/intraDayProfile/store/data/IntraDayProfileStreamDataState';
import { IntraDayProfileConfiguration } from 'widgets/intraDayProfile/store/configuration/IntraDayProfileConfiguration';
import { computeChartData } from 'widgets/intraDayProfile/chart/computeChartData';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import {
    selectIndigoMarketDataEnabled,
    selectMarketBarDataBars,
    selectRefData,
} from 'marketData/store/selectors';
import { getExternalMarketBarData } from 'marketData/hooks';
import { ColDef } from 'ag-grid-community';
import { useSymbolInBloombergSymbology } from 'symbols/symbolInfo/hooks';

export interface WidgetPanelIntradayProfileProps
    extends WidgetPanelDetailProps {
    mode: 'data' | 'stream' | 'timeSeries' | 'timelinelabels';
}

export default function WidgetPanelIntradayProfiles(
    props: WidgetPanelIntradayProfileProps
) {
    const {
        widgetState,
        mode,
        widget: { id: widgetKey },
        state,
        selection,
    } = props;

    const data = widgetState.stream.data as IntraDayProfileDataState;
    const config = widgetState.config as IntraDayProfileConfiguration;

    const bbSymbol = useSymbolInBloombergSymbology(
        selection.selectedSymbol,
        selection.symbology
    );
    if (!data || !config) {
        return <></>;
    }

    const indigoMarketDataEnabled = selectIndigoMarketDataEnabled(state.state);
    const bars = bbSymbol
        ? selectMarketBarDataBars(state.state, bbSymbol, config.bucketSize)
        : undefined;
    const refData = selectRefData(state.state, bbSymbol);
    const dapiData = getExternalMarketBarData(
        indigoMarketDataEnabled,
        bars,
        refData
    );

    const intradayProfilesData = computeChartData(
        data,
        config.bucketSize,
        dapiData,
        refData
    );

    for (const t of data.timeSeries) {
        showTime(t, '_i');
    }

    for (const b of intradayProfilesData.dataSeries) {
        showNiceDate(b, 'time');
        showNiceDate(b, 'bucketStartTime');
        showNiceDate(b, 'bucketEndTime');
    }

    for (const b of intradayProfilesData.breaksInTrading) {
        showNiceDate(b, 'from');
        showNiceDate(b, 'to');
    }
    for (const r of intradayProfilesData.rangeLabels) {
        showNiceDate(r, 'open');
        showNiceDate(r, 'close');
    }
    for (const t of intradayProfilesData.timeLineLabels) {
        showNiceDate(t, 'time');
    }

    const usingDapi = dapiData && refData?.averageVolume;

    switch (mode) {
        case 'data':
            return (
                <div className="widget-panel">
                    <div className="title">Intraday Profiles Data</div>
                    <JsonPanel data={intradayProfilesData} />
                </div>
            );
        case 'stream':
            return (
                <WidgetPanelGrid
                    key="stream"
                    data={data.timeSeries}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-stream`}
                />
            );
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={intradayProfilesData.dataSeries}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                    colDefs={getDataSeriesColDefs(usingDapi)}
                />
            );

        case 'timelinelabels':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={intradayProfilesData.timeLineLabels}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );
        default:
            return <div className="widget-panel">Unrecognised mode {mode}</div>;
    }
}

export function addIntradayProfilePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelIntradayProfiles {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render,
    });

    const renderStream = () => (
        <WidgetPanelIntradayProfiles {...detailProps} mode={'stream'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderStream,
    });

    const renderTs = () => (
        <WidgetPanelIntradayProfiles {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });

    const renderLb = () => (
        <WidgetPanelIntradayProfiles {...detailProps} mode={'timelinelabels'} />
    );
    panes.push({
        menuItem: 'Time Line Labels',
        render: renderLb,
    });
}

function getDataSeriesColDefs(usingDapi: undefined | number) {
    const fields: ColDef[] = [
        'timeDisplay',
        'bucketStartTimeDisplay',
        'bucketEndTimeDisplay',
        'auctionVolume',
        'actualTradedVolume',
        'cumulativeTradedVolume',
        'historicalTradedVolume',
        'historicalCumulativeTradedVolume',
        'historicalAuctionAndTradedVolume',
        'historicalSpread',
        'historicalVolatility',
        'barWidthPercentage',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));

    if (usingDapi) {
        fields.find((d) => d.field === 'actualTradedVolume')!.headerName =
            '(DAPI) Actual Traded Volume';
    }

    return fields;
}
